import { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff, Loader2, ShieldCheck, Smartphone } from 'lucide-react';
import { authService } from '../../services/auth.service';
import { useAuthStore } from '../../stores/authStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { toSessionUser } from '../../utils/sessionUser';

/**
 * Native login page (VSNP-653)
 *
 * Replaces the Keycloak login-required redirect. Talks to our `/auth/login`
 * endpoint which in turn uses KC's password grant. VPN-only users are shown
 * a "Use the app" screen with download links and no session is issued.
 */
export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setVSNAuth } = useAuthStore();
  const { general } = useSettingsStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appUser, setAppUser] = useState<{ displayName: string } | null>(null);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  const platformName = `${general?.companyName || 'SecureLink'} VSN+`;

  // Redirect target after successful login — preserve the attempted URL if present
  const from = (location.state as { from?: Location } | null)?.from;
  const redirectTo =
    from && typeof from === 'object' && 'pathname' in from
      ? ((from as unknown as Location).pathname || '/dashboard')
      : '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please enter your email and password.');
      return;
    }

    setError(null);
    setNeedsVerification(false);
    setAppUser(null);
    setLoading(true);

    try {
      const response = await authService.nativeLogin(email.trim(), password, rememberMe);

      if ('appUser' in response && response.appUser) {
        setAppUser({ displayName: response.displayName });
        return;
      }

      if ('token' in response && response.token) {
        setVSNAuth(
          response.token,
          response.expiresAt,
          toSessionUser(response.user),
          response.absoluteExpiresAt,
        );
        navigate(redirectTo, { replace: true });
      }
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      const message = err?.response?.data?.error?.message;
      if (code === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
      } else if (err?.response?.status === 401) {
        // The API deliberately returns a generic message for a bad credential
        // so it never leaks whether an account exists. But it also 401s for
        // conditions the user can act on — a disabled account, for one — and
        // flattening those into "Invalid email or password" sends people
        // hunting for a password problem that isn't there. Those cases are
        // only reachable *after* Keycloak has accepted the password, so the
        // caller has already proven they own the account and there is nothing
        // left to leak. Show what the server said; fall back to the generic
        // wording when it says nothing specific.
        setError(message || 'Invalid email or password.');
      } else {
        setError(message || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setResendState('sending');
    try {
      await authService.resendVerification(email.trim());
    } finally {
      setResendState('sent');
    }
  };

  // ==========================================
  // VPN-user blocked screen
  // ==========================================
  if (appUser) {
    const downloads = [
      { label: 'iOS', url: general?.appDownloadIos },
      { label: 'Android', url: general?.appDownloadAndroid },
      { label: 'macOS', url: general?.appDownloadMacos },
      { label: 'Windows', url: general?.appDownloadWindows },
    ].filter((d) => d.url);

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10 text-center">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-primary-50 flex items-center justify-center">
              <Smartphone className="w-7 h-7 text-primary-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading">
            Continue on the SecureLink App
          </h1>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            Hi {appUser.displayName}, your account is configured for VPN access via the{' '}
            <strong>SecureLinkVPN</strong> app. This management console is for platform administrators only.
          </p>
          {downloads.length > 0 && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-3">
                Download the app
              </p>
              <div className="grid grid-cols-2 gap-2">
                {downloads.map((d) => (
                  <a
                    key={d.label}
                    href={d.url!}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-center text-sm font-medium text-primary-700 bg-white border border-gray-200 rounded-md py-2 hover:bg-primary-50"
                  >
                    {d.label}
                  </a>
                ))}
              </div>
            </div>
          )}
          <button
            onClick={() => {
              setAppUser(null);
              setEmail('');
              setPassword('');
            }}
            className="text-sm text-primary-700 hover:underline"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // Email-not-verified screen
  // ==========================================
  if (needsVerification) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10">
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
              <ShieldCheck className="w-7 h-7 text-amber-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading text-center">
            Verify your email
          </h1>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed text-center">
            Please verify <strong>{email}</strong> before signing in. We sent a verification link when
            your account was set up.
          </p>
          {resendState === 'sent' ? (
            <p className="text-center text-sm text-emerald-600 mb-4">
              If an account exists, a new verification email has been sent.
            </p>
          ) : (
            <button
              onClick={handleResend}
              disabled={resendState === 'sending'}
              className="w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 mb-3"
            >
              {resendState === 'sending' ? 'Sending…' : 'Resend verification email'}
            </button>
          )}
          <button
            onClick={() => {
              setNeedsVerification(false);
              setResendState('idle');
            }}
            className="w-full text-sm text-gray-600 hover:text-gray-900 py-2"
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // ==========================================
  // Login form
  // ==========================================
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900 font-heading">
            Sign in to {platformName}
          </h1>
        </div>

        {/* VSNP-704 / VSNP-721: surface the reason for an automatic logout so
            the user isn't left wondering why they were bounced here. The two
            paths are intentionally distinct — idle is a per-user activity
            timeout (default 15 min); absolute is the org-wide hard cap on
            session lifetime (default 480 min) that fires regardless of
            activity. */}
        {new URLSearchParams(location.search).get('reason') === 'idle' && (
          <div
            role="status"
            className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
          >
            You were signed out due to inactivity. Please sign in again to continue.
          </div>
        )}
        {new URLSearchParams(location.search).get('reason') === 'absolute' && (
          <div
            role="status"
            className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
          >
            Your session reached its maximum duration. Please sign in again.
          </div>
        )}

        <div className="bg-white rounded-xl shadow-md p-8">
          <form onSubmit={handleSubmit} noValidate>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="you@example.com"
                disabled={loading}
                required
              />
            </div>

            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 pr-10 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((p) => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center mb-5">
              <input
                id="rememberMe"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                disabled={loading}
              />
              <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
                Keep me signed in for 30 days
              </label>
            </div>

            {error && (
              <div
                role="alert"
                className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p className="text-center text-xs text-gray-500 mt-6">
            Need access? Ask your administrator to invite you.
          </p>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          <Link to="/dashboard" className="hover:underline">
            {platformName}
          </Link>
        </p>
      </div>
    </div>
  );
}
